import { useEffect, useState } from "react";
import { Loader2, Plus, TrendingDown, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ledgerApi } from "@/api/mist";

const STATUS_STYLE = {
  ok: "bg-[#0d2a1c] text-[#39ff9e] border-[#39ff9e]",
  warn: "bg-[#3a2e0c] text-[#ffcf3f] border-[#ffcf3f]",
  critical: "bg-[#3a0f0f] text-[#ff6a5a] border-[#ff6a5a]",
};

export default function ApiLedgerPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prov, setProv] = useState("");
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("");

  const load = () => ledgerApi.list().then(setData).catch(() => setData({ providers: [], catalog: {} }));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const key = (prov || "").trim().toLowerCase();
    if (!key) { toast.error("Pick or name an API"); return; }
    setBusy(true);
    try {
      await ledgerApi.upsert({ provider: key, add_credit: parseFloat(amount) || 0, rate: rate ? parseFloat(rate) : undefined });
      toast.success(`${key} saved`); setProv(""); setAmount(""); setRate(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const topup = async (p) => {
    const v = window.prompt(`Add credit to ${p} ($):`, "10");
    if (!v) return;
    try { await ledgerApi.topup(p, parseFloat(v)); toast.success("Topped up"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Top-up failed"); }
  };
  const remove = async (p) => { await ledgerApi.remove(p).catch(() => {}); load(); };

  if (!data) return <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  const catalog = data.catalog || {};

  return (
    <div className="space-y-4" data-testid="api-ledger-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-2xl text-white flex items-center gap-2"><Wallet size={20} className="text-gold" /> API Credit Ledger</h3>
        <span className="font-mono text-[11px] text-white/60">Total ${Number(data.total_balance || 0).toFixed(2)}</span>
      </div>
      <p className="font-mono text-[9px] text-white/40 leading-relaxed">Auto-tracks spend across all users. The warning threshold is each API's own average daily spend — it flags when about one day of runway is left.</p>

      {/* add / top up */}
      <div className="border-2 border-black bg-charcoal rounded-xl p-3 space-y-2 shadow-[3px_3px_0px_#000]">
        <div className="flex flex-wrap gap-1.5" data-testid="ledger-catalog">
          {Object.keys(catalog).map((k) => (
            <button key={k} onClick={() => { setProv(k); setRate(String(catalog[k].rate)); }}
              className={`border-2 border-black px-2 py-1 font-mono text-[9px] font-black uppercase tracking-wide rounded ${prov === k ? "bg-gold text-black" : "bg-[#141414] text-white/60"}`}>
              {catalog[k].label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input value={prov} onChange={(e) => setProv(e.target.value)} placeholder="api key/name" data-testid="ledger-provider-input"
            className="col-span-1 bg-black text-white border-2 border-black rounded-lg px-2 py-2 font-mono text-[11px]" />
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="$ amount" data-testid="ledger-amount-input"
            className="bg-black text-white border-2 border-black rounded-lg px-2 py-2 font-mono text-[11px]" />
          <input value={rate} onChange={(e) => setRate(e.target.value)} type="number" placeholder="unit cost $" data-testid="ledger-rate-input"
            className="bg-black text-white border-2 border-black rounded-lg px-2 py-2 font-mono text-[11px]" />
        </div>
        <button onClick={add} disabled={busy} data-testid="ledger-add-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-[11px] uppercase tracking-widest py-2.5 border-2 border-black rounded-lg shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add / Save & Enter
        </button>
      </div>

      {/* providers */}
      <div className="space-y-2" data-testid="ledger-providers">
        {(data.providers || []).length === 0 && <p className="font-mono text-[10px] text-white/40 text-center py-4">No APIs tracked yet — add one above.</p>}
        {(data.providers || []).map((p) => (
          <div key={p.provider} className={`border-2 rounded-xl p-3 ${STATUS_STYLE[p.status] || STATUS_STYLE.ok}`} data-testid={`ledger-row-${p.provider}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] font-black uppercase tracking-wide">{p.label}</span>
              <span className="font-mono text-[13px] font-black">${Number(p.balance).toFixed(2)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between font-mono text-[8.5px] uppercase tracking-wide opacity-80">
              <span className="flex items-center gap-1"><TrendingDown size={10} /> ${Number(p.avg_daily).toFixed(2)}/day · {p.days_left == null ? "—" : `${p.days_left}d left`}</span>
              <span>spent ${Number(p.spent_total).toFixed(2)}</span>
            </div>
            {p.alert && <p className="mt-1 font-mono text-[9px] font-black">{p.alert}</p>}
            <div className="mt-2 flex gap-2">
              <button onClick={() => topup(p.provider)} data-testid={`ledger-topup-${p.provider}`}
                className="flex-1 border-2 border-black bg-black/40 font-mono text-[9px] font-black uppercase tracking-widest py-1.5 rounded active:translate-y-[1px]">+ Add credit</button>
              <button onClick={() => remove(p.provider)} aria-label="remove" className="border-2 border-black bg-black/40 px-2 rounded active:translate-y-[1px]"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
